import type {SlotGameStore, BaseComponentConfig, BitmapText} from "@gamzix/slot_core";

import {CONST} from "../../../src/const";

class CoinAnimationComponent extends SlotGame.src.component.BaseComponent {

    static events = {
        FLY_FINISH: "CoinAnimationComponent.FLY_FINISH",
        FLY_START: "CoinAnimationComponent.FLY_START",
        ANIMATE_FINISHED: 'COIN_ANIMATE_FINISHED'
    }

    protected TIME_MOVE = 250;
    protected TIME_DELAY_BEFORE_MOVE = 250;

    protected isShowing = false;
    protected totalWinCounter;

    protected isRestoreBonusGame: boolean = false;

    protected silverCoinArr = [];
    protected goldCoinArr = [];

    protected reelset;

    constructor(config: BaseComponentConfig) {
        super(config);

        this.status_checkers = {
            ...super.getStatusCheckers(),

            isShowing: () => this.isShowing
        }
    }

    onGameReady() {
        super.onGameReady();

        //@ts-ignore
        this.reelset = game.component.reelset;

        this.totalWinCounter = game.tree.findByName('ui.win_label');
    }

    async playCoinAnimation(label, {time, fromElement, toElement, onStart, onFinish}) {
        function setOverlayImage(textureName, view) {
            const overlayView: any = game.builder.create({type: "Sprite", texture: textureName, scale: {x: 1, y: 1}});
            overlayView.options.visible = false;
            overlayView.updateViewOptions();
            view.addChild(overlayView);

            return overlayView;
        }

        let text;

        // jackpot only on bronze coin
        if ([CONST.JACKPOT.GOLD.name, CONST.JACKPOT.SILVER.name, CONST.JACKPOT.BRONZE.name].includes(label)) {
            text = setOverlayImage(`coin_${label}`, this.view);
        } else {
            text = <BitmapText>game.builder.create(this.options.config.coinTextConfig);
            text.options.text = label;
            text.visible = false;
            text.setText(label);
            this.view.addChild(text);
        }

        await game.utils.GameUtils.playMoveAnimation(text, {
            fromElement: fromElement,
            toElement: toElement,
            time: time,
            onStart: () => {
                onStart();
                text.visible = true
            },
            onFinish: () => {
                onFinish();
                text.visible = false
            }
        })

        await game.utils.delay(0);
        game.utils.PIXIUtils.remove(text);
    }

    async startCoinAnimationFinale(): Promise<void> {
        for (let reelId = 0; reelId < CONST.COLUMNS; reelId++) {
            for (let rowId = 0; rowId < CONST.ROWS; rowId++) {
                const symbolData = this.store.gameData.reels[reelId][rowId];

                if (ReelsetComponent.src.SymbolView.bonusGameConfig.coinSymbolIds.includes(String(symbolData?.symbol)) && symbolData?.value) {
                    const symbol = this.reelset.getReel(reelId).getSymbol(rowId);
                    const coinValue = symbolData.value * this.store.gameData.bet;
                    let value = this.store.toFormatMoney(coinValue);

                    // jackpot only on bronze coin
                    if (symbol.id == CONST.SYMBOLS.COIN) {
                        if (symbolData.value == CONST.JACKPOT.GOLD.multiplier) {
                            value = CONST.JACKPOT.GOLD.name;
                        }

                        if (symbolData.value == CONST.JACKPOT.SILVER.multiplier) {
                            value = CONST.JACKPOT.SILVER.name;
                        }

                        if (symbolData.value == CONST.JACKPOT.BRONZE.multiplier) {
                            value = CONST.JACKPOT.BRONZE.name;
                        }
                    }

                    await game.utils.delay(this.TIME_DELAY_BEFORE_MOVE);

                    await this.playCoinAnimation(value, {
                        time: this.TIME_MOVE,
                        fromElement: symbol,
                        toElement: this.totalWinCounter,
                        onStart: () => {
                            symbolData.value = null;
                            symbol.removeOverlayView();
                            game.emit(CoinAnimationComponent.events.FLY_START);
                        },
                        onFinish: () => {
                            this.store.gameData.visualWinAmount += coinValue;
                            this.totalWinCounter.setText(this.store.toFormatMoney(this.store.gameData.visualWinAmount));
                            game.emit(CoinAnimationComponent.events.FLY_FINISH);
                        }
                    });
                }
            }
        }

        game.emit(CoinAnimationComponent.events.ANIMATE_FINISHED);
    }

    async runCoinArray(coinArr, targetCoin) {
        for (const coinItem of coinArr) {

            const coinValue = coinItem.symbolData.value * this.store.gameData.bet;
            let value = this.store.toFormatMoney(coinValue);

            await game.utils.delay(this.TIME_DELAY_BEFORE_MOVE);

            await this.playCoinAnimation(value, {
                time: this.TIME_MOVE,
                fromElement: coinItem.symbolView,
                toElement: targetCoin.symbolView,
                onStart: () => {
                    game.emit(CoinAnimationComponent.events.FLY_START);
                    if (!targetCoin.symbolView.hasOwnProperty('counter')) {
                        targetCoin.symbolView.counter = 0;
                    }
                },
                onFinish: () => {
                    game.emit(CoinAnimationComponent.events.FLY_FINISH);

                    //let's make the data valid for mathematics based on them
                    let flySymbolValue;
                    const jackpotValues = {
                        platinum: CONST.JACKPOT.PLATINUM.multiplier,
                        gold: CONST.JACKPOT.GOLD.multiplier,
                        silver: CONST.JACKPOT.SILVER.multiplier,
                        bronze: CONST.JACKPOT.BRONZE.multiplier,
                    };
                    if ([ "platinum", "gold", "silver", "bronze" ].includes(value)) {
                        flySymbolValue = Number(this.store.toFormatMoney(this.store.gameData.bet)) * jackpotValues[value];
                    } else {
                        flySymbolValue = Number(value);
                    }

                    const currencyDecimal = Math.pow(10, this.store.currencyDecimal)
                    const updateValue = this.store.toFormatMoney((Number(flySymbolValue) + Number(targetCoin.symbolView.counter)) * currencyDecimal)
                    targetCoin.symbolView.setOverlayView(updateValue);
                    targetCoin.symbolView.counter += flySymbolValue
                },
            });
        }
    }

    async startCoinAnimation(): Promise<void> {
        const bronzeCoinArr = [];

        for (let reelId = 0; reelId < CONST.COLUMNS; reelId++) {
            for (let rowId = 0; rowId < CONST.ROWS; rowId++) {
                const symbolData = this.store.gameData.reels[reelId][rowId];
                const symbolView = this.reelset.getReel(reelId).getSymbol(rowId);

                if (symbolData?.symbol == '0') {
                    bronzeCoinArr.push({ symbolData, symbolView });
                }

                if (symbolData?.symbol == '11' && !symbolView.isCounted) {
                    this.silverCoinArr.push({ symbolData, symbolView });
                }

                if (symbolData?.symbol == '12' && !symbolView.isCounted) {
                    this.goldCoinArr.push({ symbolData, symbolView });
                }
            }
        }

        // run count silver coins
        for (const silverItem of this.silverCoinArr) {
            if (silverItem.symbolView.isCounted) continue;

            await this.runCoinArray(bronzeCoinArr, silverItem);

            silverItem.symbolView.isCounted = true;
        }

        // run count gold coins
        for (const goldItem of this.goldCoinArr) {
            if (goldItem.symbolView.isCounted) continue;

            await this.runCoinArray(bronzeCoinArr, goldItem);
            await this.runCoinArray(this.silverCoinArr, goldItem);

            goldItem.symbolView.isCounted = true;
        }
    }


    async show() {
        this.isShowing = true;

        this.onChangeOrientation();
    }

    async hide() {
        this.isShowing = false;

        this.onChangeOrientation();
    }

    reset() {
        this.silverCoinArr.length = 0;
        this.goldCoinArr.length = 0;
    }


    get isRunBonusAnimation(): boolean {
        //@ts-ignore
        return this.store.gameData.reels.flatMap((s) => s).filter(s => s !== null && [11, 12].includes(s.symbol)).length > 0
    }

    get store(): SlotGameStore {
        return <SlotGameStore>game.store;
    }

    getFSMHandlers(): any {
        return {
            START: {
                checkRestoreBonusGame: {
                    on: async () => {
                        this.isRestoreBonusGame = true;
                    },
                },
            },
            IDLE: {
                enter: {
                    on: async () => {
                        if (this.isRestoreBonusGame) {
                            for (let reelId = 0; reelId < CONST.COLUMNS; reelId++) {
                                for (let rowId = 0; rowId < CONST.ROWS; rowId++) {
                                    const symbolData = this.store.gameData.reels[reelId][rowId];
                                    const symbolView = this.reelset.getReel(reelId).getSymbol(rowId);

                                    if (symbolData?.value) {
                                        symbolView.counter = Number(symbolData.value);
                                        symbolView.isCounted = true;
                                        symbolView.setOverlayView();
                                    }
                                }
                            }
                        }
                    },
                },
            },
            SPIN: {
                start: {
                    on: async () => {
                        this.isRestoreBonusGame = false;
                    }
                },
                exit: {
                    on: async () => {
                        if (this.store.gameData.bonusGame.isPlaying() && this.isRunBonusAnimation) {
                            await this.show();
                            await this.startCoinAnimation();
                            await game.utils.delay(50);
                            await this.hide();
                            await game.utils.delay(50);
                        }

                        if (this.store.gameData.bonusGame.isFinished()) {
                            await this.show();
                            await this.startCoinAnimationFinale();

                            await game.utils.delay(1000);
                            await this.hide();

                            this.reset();
                        }
                    }
                }
            }
        }
    }
}

export {CoinAnimationComponent}

//@ts-ignore
Game.src.component.CoinAnimationComponent = CoinAnimationComponent;